/**
 * 级联选择相关的工具函数 (Vue2版本)
 * 提供级联选择处理、路径构建、选项获取等功能
 */
import { FieldType } from './enum.js'
import { getName } from './useFast.js'

/**
 * 处理级联选择
 * @param {Object} option 选中的选项
 * @param {Object} category 级联分类
 * @param {Array} filterOptions 筛选选项数组
 */
export function handleCascadeSelect(option, category, filterOptions) {
  // 初始化级联对象
  if (!category.cascadeObj) {
    category.cascadeObj = {}
  }

  // 获取当前选中的级别
  const levelList = getCascadeList(option, category)

  if (levelList.length === 0) {
    return
  }

  // 清除当前级别及以下的选择
  category.cascadeObj = {}
  levelList.forEach((item, index) => {
    category.cascadeObj[index + 1] = {
      id: item.id,
      name: item.name,
      nickname: item.nickname,
      options: item.options || []
    }
  })

  // 构建完整路径
  const selectedItem = {
    id: option.id,
    name: option.name,
    nickName: option.nickname
  }
  category.value = [selectedItem]

  // 更新筛选选项
  filterOptions.forEach(item => {
    if (item.id === category.id) {
      item.value = category.value
      item.cascadeObj = category.cascadeObj
    }
  })
}

/**
 * 获取选中选项的完整路径
 * @param {Object} option 选项
 * @param {Object} category 级联分类
 * @returns {Array} 从根节点到选中选项的完整路径数组
 */
export function getCascadeList(option, category) {
  // 检查是否是一级选项
  const level1 = category.options?.find(opt => opt.id === option.id)
  if (level1) {
    return [level1]
  }

  // 检查是否是二级选项
  for (const level1Option of category.options || []) {
    const level2 = level1Option.options?.find(opt => opt.id === option.id)
    if (level2) {
      return [level1Option, level2]
    }

    // 检查是否是三级选项
    for (const level2Option of level1Option.options || []) {
      const level3 = level2Option.options?.find(opt => opt.id === option.id)
      if (level3) {
        return [level1Option, level2Option, level3]
      }
    }
  }

  return []
}

/**
 * 构建级联路径
 * @param {Object} category 级联分类
 * @returns {Array} 路径数组
 */
export function buildCascadePath(category) {
  const path = []

  for (let level = 1; level <= 3; level++) {
    const cascadeItem = category.cascadeObj[level]
    if (cascadeItem) {
      path.push(getName(cascadeItem))
    }
  }

  return path
}

/**
 * 获取当前级联选择的选项列表
 * @param {Object} category 级联分类
 * @param {number} level 级别（1-3）
 * @returns {Array} 选项列表
 */
export function getCascadeOptions(category, level) {
  if (level === 1) {
    return category.options || []
  }

  const parentLevel = level - 1
  const parentItem = category.cascadeObj[parentLevel]

  if (!parentItem) {
    return []
  }

  return parentItem.options || []
}

/**
 * 检查选项是否被选中
 * @param {Object} option 选项
 * @param {Object} category 级联分类
 * @param {number} level 级别
 * @returns {boolean} 是否被选中
 */
export function isCascadeOptionSelected(option, category, level) {
  const cascadeItem = category.cascadeObj[level]
  return cascadeItem && cascadeItem.id === option.id
}

/**
 * 获取级联选择的完整路径文本
 * @param {Object} category 级联分类
 * @returns {string} 路径文本
 */
export function getCascadePathText(category) {
  if (category.fieldType === FieldType.SINGLE_CASCADE) {
    const values = category.value
    if (values && values.length > 0) {
      return values[0]?.name || ''
    }
  }
  return ''
}

/**
 * 获取级联的当前选择状态
 * @param {Object} category 级联分类
 * @returns {Object} 选择状态对象
 */
export function getCascadeSelectionState(category) {
  return {
    pathText: getCascadePathText(category),
    selectedLevels: Object.keys(category.cascadeObj || {}).length,
    finalValue: category.value || [],
    cascadeObj: category.cascadeObj || {}
  }
}

/**
 * useCascade hooks (Vue2版本)
 * 返回级联选择相关的所有功能函数
 */
export default function useCascade() {
  return {
    // 核心功能
    handleCascadeSelect,
    getCascadeOptions,
    isCascadeOptionSelected,

    // 辅助功能
    getCascadePathText,
    getCascadeSelectionState,
    getCascadeList,
    buildCascadePath
  }
}
